import * as React from 'react';
import { WithStyles, withStyles } from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { MonthviewStyle } from './monthview.style';
import { IDayData, IHandoverData } from '../calendar.types';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { ISchedule, IScheduleEntry } from '@app-types/websocket/schedule.types';
import { generateHandoverData } from '../helper';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { IEvent, ILocalEvent } from '@app-types/websocket/event.types';
import DayEvent from './DayEvent';
import DayImportedEvent from './DayImportedEvent';
import { FamilyMemberType } from '@services/family/family.constants';
import { getOS } from '@helpers/osDetection';
import CordovaContext from '../../../cordovaContext';
import { HELP_TOPICS } from '@constants/suggestionTypes';
import { IExternalCalenderEvent } from '@app-types/api/externalCalendar.types';
import DayExternalEvent from './DayExternalEvent';
import { collectEvents, IDayEventLayout, layoutRowEvents, segmentStyle } from './monthEventLayout';

const tn: TranslateNew = TranslateNew.getInstance();
const os = getOS();
declare const TapticEngine: any;

interface IProps extends WithStyles<typeof MonthviewStyle> {
  isSelectedForEdit: boolean;
  isFocus: boolean;
  isHighlight: boolean;
  swipeEditTransaction?: (action:"START"|"COMMIT") => void;
  swipeEdit?: (year:number,month:number,date:number, swipeDays:number, swipdeStartSide:"LEFT"|"RIGHT") => void;
  setFocusDate: (year: number, month: number, date: number, position:"LEFT"|"RIGHT"|"NAN", isLongPress:boolean, originalDay?:Date) => void;
  localization: 'se' | 'en';
  standaAloneCalendar?: boolean;
  size: 'small' | 'medium' | 'large';
  data: IDayData;
  isEditing: boolean;

  selectIDayInsteadOfMenuPop?: boolean;
  userId: number;
  family: IFamilyMember[];
  schoolAge: number;
  scheduleOne: ISchedule | undefined;
  scheduleTwo: ISchedule | undefined;
  scheduleThree: ISchedule | undefined;

  idPrefix?: string;

  events: IEvent[];
  importedEvents: ILocalEvent[];
  externalEvents: IExternalCalenderEvent[]|undefined;
  // The month lays out whole week rows so bars can span days; without it the day lays out its own events
  eventLayout?: IDayEventLayout;
  // Days crossed by the same multi-day bars start their events at the same height: the handover rows
  // of the other such days (stacked invisibly), and in edit mode the space of old-schedule bars other days show
  alignHandoverRows?: JSX.Element[][];
  reserveOldBars?: boolean;
  // The week row this day is in, for the month to measure the room under the header and pick the row's lane
  // height; denseChipStyle is what the month picked when a day of the row has more events than fit
  laneRow?: number;
  denseChipStyle?: React.CSSProperties;
  needBottomRow?: boolean;
  needLeftBorder?: boolean;
  hideTopBorder?: boolean;
  hideEvents?: boolean;
  showWeekNumber?: boolean;
  onlyWeeksOf?: number;

  popMenu: (ref: HTMLDivElement | null) => void;
  dismissHelpNotice?: (notice:string) => void;  

  holidays:{ [key:string]:{title:string, type:"HOLIDAY"|"SPECIAL_DAY"}; };
  hasDismissedLongPress:boolean;
  eventStyle?: React.CSSProperties;
}

interface IState {
  something: boolean;
}

class Day extends React.Component<IProps, IState> {
  private timeoutId: NodeJS.Timeout | null = null;
  private longPressThreshold: number = 500; // milliseconds
  private moveThreshold: number = 10; // pixels

  private longPressActivated: boolean = false;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private swipeDays: number = 0;
  private dragMoved: boolean = false;
  private isMouseDragging: boolean = false;
  private suppressNextClick: boolean = false;

  private swipeStartType: "LEFT"|"RIGHT" = "LEFT";

  private thisDayRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: IProps) {
    super(props);

    this.state = {
      something: false,
    };
  }

  public componentWillUnmount() {
    this.clearLongPressTimer();
    this.detachMouseHandlers();
  }

  public render() {
    const {
      classes,
      data,
      scheduleOne,
      scheduleTwo,
      scheduleThree,
      isSelectedForEdit,
      isFocus,
      size,
      events,
      family,
      isEditing,
      isHighlight,
      hideEvents,
      importedEvents,
    } = this.props;
    const today = new Date();
    const isToday: boolean = today.getDate() === data.date && data.month === today.getMonth() && data.year === today.getFullYear();

    const start: number = data.startTime;
    const end: number = data.endTime;

    let layout: IDayEventLayout | undefined;
    if (!hideEvents) {
      layout = this.props.eventLayout
        || layoutRowEvents([data], collectEvents(start, end, events, importedEvents, [this.props.externalEvents]))[0];
    }

    const dot: JSX.Element = (
      <div
        className={size === 'large' ? classes.dayEventDotHidden : classes.dayEventDot}
        style={{ opacity: layout !== undefined && layout.hasEvents ? 1 : 0 }}
      />
    );

    let dayStyle = '';

    if (isEditing && isSelectedForEdit) {
      dayStyle = classes.dayBorderSelected;
    } else if (isHighlight) {
      dayStyle = classes.dayBorderHighlight;
    } else if (isFocus && !isEditing) {
      if (size === 'large') {
        dayStyle = classes.dayBorderTodayLarge;
      } else {
        dayStyle = classes.dayBorderTodayMedium;
      }
    } else if (size === 'large') {
      if (this.props.hideTopBorder) {
        if (this.props.needLeftBorder) {
          dayStyle = classes.dayBorder + ' ' + classes.dayBorderTopRow + ' ' + classes.dayBorderLeftRow;
        } else {
          dayStyle = classes.dayBorder + ' ' + classes.dayBorderTopRow;
        }
      } else if (this.props.needBottomRow) {
        dayStyle = classes.dayBorder + ' ' + classes.dayBorderLastDays;
      } else if (this.props.needLeftBorder) {
        dayStyle = classes.dayBorder + ' ' + classes.dayBorderLeftRow;
      } else {
        dayStyle = classes.dayBorder;
      }
    } else {
      dayStyle = classes.dayNoBorder;
    }

    const scheduleEventsOne = scheduleOne && this.scheduleLine(scheduleOne, start, end, true, isToday, this.props.onlyWeeksOf);
    const scheduleEventsTwo = scheduleTwo && this.scheduleLine(scheduleTwo, start, end, false, isToday, this.props.onlyWeeksOf);
    const scheduleEventsThree = scheduleThree && this.scheduleLine(scheduleThree, start, end, false, isToday, this.props.onlyWeeksOf);

    const scheduleLineOne = scheduleEventsOne ? scheduleEventsOne.line : undefined;
    const scheduleLineTwo = scheduleEventsTwo ? scheduleEventsTwo.line : undefined;
    const scheduleLineThree = scheduleEventsThree ? scheduleEventsThree.line : undefined;

    const scheduleLocOne = scheduleEventsOne ? scheduleEventsOne.events : undefined;
    const scheduleLocTwo = scheduleEventsTwo ? scheduleEventsTwo.events : undefined;
    const scheduleLocThree = scheduleEventsThree ? scheduleEventsThree.events : undefined;

    let dayType:"PARENT_A"|"PARENT_B"|"NAN"|"HANDOVER"|"VISITATION" = "NAN";
    if (isEditing) { // only one calendar is showing
      if (scheduleEventsOne !== undefined) {dayType = scheduleEventsOne.dayType;} 
    }

    const id = (this.props.idPrefix ? this.props.idPrefix : '') + data.year + '-' + data.month + '-' + data.date;

    const eventElements: JSX.Element[] = [];
    if (size === 'large' && layout !== undefined) {
      const chipStyle = this.props.denseChipStyle ? { ...this.props.eventStyle, ...this.props.denseChipStyle } : this.props.eventStyle;
      if (this.props.laneRow !== undefined && layout.lanes.length > 0) {
        // marks where the lanes start: the month measures the room from here to the bottom of the day
        eventElements.push(<div key="lanes" className={classes.lanesStart} data-lane-row={this.props.laneRow} data-lanes={layout.lanes.length} />);
      }
      for (let i = 0; i < layout.lanes.length; i++) {
        const segment = layout.lanes[i];
        if (segment === null) {
          // an empty lane: a bar from an earlier day runs through it, or it is a gap that keeps the bars below in line
          eventElements.push(
            <div key={i} className={classes.event} style={{ ...chipStyle, visibility: 'hidden' }}>{'\u00a0'}</div>
          );
          continue;
        }
        const e = segment.item;
        const shape = segmentStyle(segment);
        if (e.event !== null) {
          eventElements.push(
            <DayEvent
              key={i}
              shape={shape}
              evenDay={data.date % 2 === 0}
              event={e.event}
              family={family}
              hide={hideEvents}
              style={chipStyle}
              userId={this.props.userId}
            >
              {e.event.title}
            </DayEvent>
          );
        } else if (e.importedEvent !== null) {
          eventElements.push(
            <DayImportedEvent key={i} shape={shape} event={e.importedEvent} style={chipStyle}
              hide={hideEvents}>
              {e.importedEvent.title}
            </DayImportedEvent>
          );
        } else if (e.externalEvent !== null) {
          eventElements.push(
            <DayExternalEvent key={i} shape={shape} event={e.externalEvent} style={chipStyle}
              hide={hideEvents} family={family} userId={this.props.userId}>
              {e.externalEvent.title}
            </DayExternalEvent>
          );
        }
      }
    }
    let dateStyle: string = classes.dayDate;
    const dayString =
      data.year + '-' + (data.month < 9 ? '0' + (data.month + 1) : data.month + 1) + '-' + (data.date < 10 ? '0' + data.date : data.date);

    if (isToday) {
      dateStyle = dateStyle + ' ' + classes.dayTodayHeader;
    } else if (this.props.holidays[dayString] !== undefined) {
      if (this.props.holidays[dayString].type === 'HOLIDAY') {
        dateStyle = dateStyle + ' ' + classes.dayIsHoliday;
      } else if (this.props.holidays[dayString].type === 'SPECIAL_DAY') {
        dateStyle = dateStyle + ' ' + classes.dayIsSpecialDay;
      }
    }

    const interactionCursor = this.props.isEditing && this.props.swipeEdit !== undefined ? 'ew-resize' : 'pointer';

    const alignHandoverRows = this.props.alignHandoverRows;

    return (
      <div
        className={classes.day}
        ref={this.thisDayRef}
        style={this.props.showWeekNumber ? { width: 'calc((100% / 7) - 3px)' } : {}}
        id={id}
      >
        <div className={classes.dayBox + ' ' + dayStyle} />
        <div className={dateStyle}>{data.date}</div>
        {dot}
        {scheduleLineOne}
        {scheduleLineTwo}
        {scheduleLineThree}
        {alignHandoverRows === undefined
          ? <>
              {scheduleLocOne}
              {scheduleLocTwo}
              {scheduleLocThree}
            </>
          : <div className={classes.alignedHandovers}>
              <div className={classes.alignedHandoversLayer}>
                {scheduleLocOne}
                {scheduleLocTwo}
                {scheduleLocThree}
              </div>
              {alignHandoverRows.map((rows, k) => (
                <div key={k} className={classes.alignedHandoversLayer} style={{ visibility: 'hidden' }} aria-hidden={true}>{rows}</div>
              ))}
            </div>}
        {eventElements}
        <div className={classes.daySelectedEditMarkSpace}>
          <CheckBoxIcon className={isSelectedForEdit && isEditing ? classes.daySelectedMarkSelected : classes.daySelectedMarkUnselected} />
        </div>
        <div style={{position:'absolute', left:0, top:0, width:'50%', height: '100%', zIndex:10, cursor: interactionCursor}}         
          onTouchStart={(e) => this.handleTouchStart(e, "LEFT")} onTouchMove={this.handleTouchMove} onTouchEnd={(e) => this.handleTouchEnd(e)}
          onMouseDown={(e) => this.handleMouseDown(e, "LEFT")}
          onClick={() => this.handleClick(data.year, data.month, data.date, (dayType==="HANDOVER"||dayType==="VISITATION"),"LEFT")} />
        <div style={{position:'absolute', right:0, top:0, width:'50%', height: '100%', zIndex:10, cursor: interactionCursor}}
          onTouchStart={(e) => this.handleTouchStart(e, "RIGHT")} onTouchMove={this.handleTouchMove} onTouchEnd={(e) => this.handleTouchEnd(e)}
          onMouseDown={(e) => this.handleMouseDown(e, "RIGHT")}
          onClick={() => this.handleClick(data.year, data.month, data.date, (dayType==="HANDOVER"||dayType==="VISITATION"), "RIGHT")} />
      </div>
    );
  }

  private handleClick = (year:number, month:number, date:number, hasHandover:boolean, side:"LEFT"|"RIGHT") => {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }

    if (hasHandover && this.props.isEditing && !this.props.isSelectedForEdit && this.props.selectIDayInsteadOfMenuPop !== true) {  
      this.props.popMenu(this.thisDayRef.current);
    } else {
      this.props.setFocusDate(year, month, date, side, false);
    }
  }

  private handleTouchStart = (event: React.TouchEvent<HTMLDivElement>, side:"LEFT"|"RIGHT") => {
    this.beginSwipe(side, event.touches[0].clientX, event.touches[0].clientY, true);
  };

  private handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    this.trackSwipe(event.touches[0].clientX, event.touches[0].clientY);
  };

  private handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (this.props.swipeEditTransaction !== undefined) {this.props.swipeEditTransaction("COMMIT");}
    if (this.touchStartX > 0) {
      this.clearLongPressTimer();
      if (this.longPressActivated) {

        if (this.props.hasDismissedLongPress === false && this.props.dismissHelpNotice !== undefined) {
          this.props.dismissHelpNotice(HELP_TOPICS.LONGCLICK);
        }

        if (!this.props.isEditing) {
          this.props.setFocusDate(this.props.data.year, this.props.data.month, this.props.data.date, "NAN", true);
        }
        this.props.popMenu(this.thisDayRef.current);
        // console.log('End of long press, perform long press action');
        // event.preventDefault();
        event.stopPropagation();
        this.resetSwipeTracking();
      } else if (this.swipeDays !== 0) {
        this.swipeDays = 0;
        // event.preventDefault();
        event.stopPropagation();
        this.resetSwipeTracking();
      } else {
        // console.log('Normal touch');
        this.resetSwipeTracking();
      }
    }
  };

  private handleMouseDown = (event: React.MouseEvent<HTMLDivElement>, side:"LEFT"|"RIGHT") => {
    if (event.button !== 0 || !this.props.isEditing || this.props.swipeEdit === undefined) {
      return;
    }

    this.isMouseDragging = true;
    this.beginSwipe(side, event.clientX, event.clientY, false);
    window.addEventListener('mousemove', this.handleWindowMouseMove);
    window.addEventListener('mouseup', this.handleWindowMouseUp);
    window.addEventListener('blur', this.handleWindowMouseCancel);
  };

  private handleWindowMouseMove = (event: MouseEvent) => {
    if (!this.isMouseDragging) {
      return;
    }
    this.trackSwipe(event.clientX, event.clientY);
  };

  private handleWindowMouseUp = () => {
    this.finishMouseDrag();
  };

  private handleWindowMouseCancel = () => {
    this.finishMouseDrag();
  };

  private beginSwipe = (side:"LEFT"|"RIGHT", startX:number, startY:number, enableLongPress:boolean) => {
    this.clearLongPressTimer();
    this.swipeStartType = side;
    if (this.props.swipeEditTransaction !== undefined) {this.props.swipeEditTransaction("START");}
    this.swipeDays = 0;
    this.dragMoved = false;
    this.longPressActivated = false;
    this.touchStartX = startX;
    this.touchStartY = startY;

    if (!enableLongPress) {
      return;
    }

    this.timeoutId = setTimeout(() => {
      this.longPressActivated = true;
      if (os === 'ANDROID' && navigator.vibrate) {
        navigator.vibrate(20);
      } else if (os === 'IOS' ) {
        const isCordovaApp = this.context;
        if (isCordovaApp) {
          TapticEngine.unofficial.weakBoom();  
        }
      }
      // console.log('Long press activated');
    }, this.longPressThreshold);
  };

  private trackSwipe = (moveX:number, moveY:number) => {
    if (this.touchStartX > 0) {
      let adjustedMoveX = moveX;
      let deltaX = adjustedMoveX - this.touchStartX;
      let deltaXabs = Math.abs(deltaX);
      const deltaY = Math.abs(moveY - this.touchStartY);

      if (deltaX > 10 && window.innerWidth - adjustedMoveX < 10) {
        adjustedMoveX = adjustedMoveX + 10;
        deltaX = adjustedMoveX - this.touchStartX;
        deltaXabs = Math.abs(deltaX);
      } else if (deltaX < -10 && adjustedMoveX < 10) {
        adjustedMoveX = adjustedMoveX - 10;
        deltaX = adjustedMoveX - this.touchStartX;
        deltaXabs = Math.abs(deltaX);
      }

      if ((deltaXabs < this.moveThreshold && deltaY > this.moveThreshold) || (deltaXabs > this.moveThreshold && deltaY > 50)) {
        this.clearLongPressTimer();
        this.longPressActivated = false;
        this.touchStartX = 0;
        this.touchStartY = 0;
      } else if (deltaXabs > this.moveThreshold) { 
        this.clearLongPressTimer();
        this.longPressActivated = false;
        this.dragMoved = true;

        const screenWidth = window.innerWidth;
        let daysSwipedNonRounded = (((deltaX / screenWidth) * 100)/14.28);
        if (daysSwipedNonRounded > 0) {
          daysSwipedNonRounded = daysSwipedNonRounded-0.2;
        } else {
          daysSwipedNonRounded = daysSwipedNonRounded+0.2;
        }

        const daysSwiped = Math.round(daysSwipedNonRounded);
        
        if (!this.props.isEditing && this.swipeDays !== daysSwiped) {
          this.props.setFocusDate(this.props.data.year, this.props.data.month, this.props.data.date, "RIGHT", false);
          this.swipeDays = daysSwiped;
        } else if (this.swipeDays !== daysSwiped && this.props.isEditing && this.props.swipeEdit !== undefined) {
          this.props.swipeEdit(this.props.data.year, this.props.data.month, this.props.data.date, daysSwiped, this.swipeStartType);
          this.swipeDays = daysSwiped;
        }
      }
    }
  };

  private finishMouseDrag = () => {
    if (!this.isMouseDragging) {
      return;
    }

    if (this.props.swipeEditTransaction !== undefined) {this.props.swipeEditTransaction("COMMIT");}
    this.detachMouseHandlers();
    this.isMouseDragging = false;

    if (this.touchStartX > 0) {
      this.clearLongPressTimer();
      if (this.dragMoved || this.swipeDays !== 0) {
        this.suppressNextClick = true;
      }
    }

    this.swipeDays = 0;
    this.resetSwipeTracking();
  };

  private clearLongPressTimer = () => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  };

  private resetSwipeTracking = () => {
    this.longPressActivated = false;
    this.dragMoved = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
  };

  private detachMouseHandlers = () => {
    window.removeEventListener('mousemove', this.handleWindowMouseMove);
    window.removeEventListener('mouseup', this.handleWindowMouseUp);
    window.removeEventListener('blur', this.handleWindowMouseCancel);
  };

  private scheduleLine(
    schedule: ISchedule | undefined,
    start: number,
    end: number,
    primarySchedule: boolean,
    isToday: boolean,
    onlyWeeksOf?: number
  ): { dayType:"PARENT_A"|"PARENT_B"|"NAN"|"HANDOVER"|"VISITATION", line: JSX.Element; events: JSX.Element[]} {
    const { classes, family, size, isEditing, hideEvents, userId } = this.props;

    let dayType:"parent_A"|"PARENT_B"|"NAN"|"HANDOVER"|"VISITATION" = "NAN";
    let custodyBarStyle = classes.custodyBar
    if (isEditing) {
      if (this.props.standaAloneCalendar) {
        custodyBarStyle = classes.custodyBarEditExtreme
      } else if (!primarySchedule) {
        if (this.props.isHighlight) {
          if (hideEvents) {
            custodyBarStyle = classes.custodyBarEditLarge+" "+classes.custodyBarOld;
          } else {
            custodyBarStyle = classes.custodyBarEdit+" "+classes.custodyBarOld;
          }
        } else if (this.props.reserveOldBars && !hideEvents) {
          // as tall as on the days that show it, but invisible, so this day's events line up with theirs
          custodyBarStyle = classes.custodyBarEdit+" "+classes.custodyBarOld+" "+classes.custodyBarReserved;
        } else {
          custodyBarStyle = classes.custodyBarHidden;
        }
      } else if (hideEvents) {
        custodyBarStyle = classes.custodyBarEditLarge
      } else {
        custodyBarStyle = classes.custodyBarEdit;
      }
    }

    if (schedule === undefined) {
      return { line: <div className={classes.custodyBar} />, events: [], dayType };
    } else {
      let colorKids: string = '#00ff00';
      let colorA: string = '#ff0000';
      let colorB: string = '#0000ff';
      let opacityA: number = 1;
      let opacityB: number = 1;
      let hideA: boolean = false;
      let hideB: boolean = false;
      let parentAName: string = '';
      let parentBName: string = '';
      let isParent: boolean = false;
      const childrenNames: string[] = [];

      for (const mem of family) {
        if (mem.id === schedule.data.parentA) {
          colorA = mem.color;
          if (onlyWeeksOf !== undefined && mem.id !== onlyWeeksOf) {
            hideA = true;
          }
          parentAName = mem.firstName;
        } else if (mem.id === schedule.data.parentB) {
          colorB = mem.color;
          if (onlyWeeksOf !== undefined && mem.id !== onlyWeeksOf) {
            hideB = true;
          }
          parentBName = mem.firstName;
        } else if (schedule.data.children.indexOf(mem.id) !== -1) {
          if (mem.id === schedule.data.children[0]) {
            colorKids = mem.color;
          }
          childrenNames.push(mem.firstName);
        }
        if (mem.id === userId && mem.role === FamilyMemberType.PARENT) {
          isParent = true;
        }
      }

      const childrenName: string = childrenNames.join(', ');

      // When showing only my weeks, my own weeks are recoloured to the children's
      // colour, so label them with the children's name(s) instead of my own. A parent
      // picked by an important adult keeps their own colour and name.
      const showsOwnWeeks: boolean = isParent && onlyWeeksOf === userId;
      if (hideA) {
        colorA = colorA + '00';
        opacityA = 0;
        if (showsOwnWeeks) {
          colorB = colorKids;
          parentBName = childrenName;
        }
      } else if (hideB) {
        colorB = colorB + '00';
        opacityB = 0;
        if (showsOwnWeeks) {
          colorA = colorKids;
          parentAName = childrenName;
        }
      }

      const dataA = generateHandoverData(schedule.data.scheduleEntriesParentA, schedule.data.parentA, start, end);
      const dataB = generateHandoverData(schedule.data.scheduleEntriesParentB, schedule.data.parentB, start, end);

      if (dataA.hasWholeDay) {
        return {
          line: (
            <div className={custodyBarStyle}>
              <div className={classes.custodyFullDay} style={{ backgroundColor: colorA, opacity: opacityA }} >
                {(dataA.writeName && size === 'large' && !isEditing) && <span style={{ fontSize: 10 }}>{parentAName}</span>}
              </div>
            </div>
          ),
          dayType:"PARENT_A",
          events: [],
        };
      } else if (dataB.hasWholeDay) {
        return {
          line: (
            <div className={custodyBarStyle}>
              <div className={classes.custodyFullDay} style={{ backgroundColor: colorB, opacity: opacityB }} >
                {(dataB.writeName && size === 'large' && !isEditing)  && <span style={{ fontSize: 10 }}>{parentBName}</span>}
              </div>
            </div>
          ),
          dayType:"PARENT_B",
          events: [],
        };
      } else {
        let morning: JSX.Element | null = null;
        if (dataA.hasMorning) {
          morning = (
            <div className={classes.custodyMorning} style={{backgroundColor:colorA, width:(this.props.size !== "large" && !isEditing)?'40%':'15%'}}>
              {isEditing ? <span style={{ paddingRight: 2, zIndex: 1, fontSize: 10 }}>{parentAName}</span> : ''}
            </div>
          );
        } else if (dataB.hasMorning) {
          morning = (
            <div className={classes.custodyMorning} style={{backgroundColor:colorB, width:(this.props.size !== "large" && !isEditing)?'40%':'15%'}}>
              {isEditing ? <span style={{ paddingRight: 2, zIndex: 1, fontSize: 10 }}>{parentBName}</span> : ''}
            </div>
          );
        }

        let visit: JSX.Element | null = null;
        let events: JSX.Element[] = [];
        if (dataA.hasVisitation) {
          dayType = "VISITATION";
          visit = <div className={classes.custodyVisit} style={{ backgroundColor: colorA, 
            fontWeight: isEditing ? 700 : 400, left:(this.props.size !== "large" && !isEditing)?'40%':'25%', transform: 'translateY(0px)', 
            minHeight:'100%',maxHeight:'100%', width:(this.props.size !== "large" && !isEditing)?'20%':'50%', borderRadius:'3px'}} />;
          if (size === 'large' && !isEditing) {
            events = handoverRows(dataA.handovers, schedule, this.props.schoolAge, classes.dayHandoverLocation, isEditing);
          }
        } else if (dataB.hasVisitation) {
          dayType = "VISITATION";
          visit = <div className={classes.custodyVisit} style={{ backgroundColor: colorB, fontWeight: isEditing ? 700 : 400, 
            left:(this.props.size !== "large" && !isEditing)?'40%':'25%', width:(this.props.size !== "large" && !isEditing)?'20%':'50%', 
            transform: 'translateY(0px)', minHeight:'100%',maxHeight:'100%', borderRadius:'3px'}} />;
          if (size === 'large' && !isEditing) {
            events = handoverRows(dataB.handovers, schedule, this.props.schoolAge, classes.dayHandoverLocation, isEditing);
          }
        } else if (size === 'large' && ((dataA.hasMorning && dataB.hasNight) || (dataB.hasMorning && dataA.hasNight))) {
          dayType = "HANDOVER";
          const handoverLocation:JSX.Element = (dataA.handovers.length === 1
            ? dataA.handovers[0].atSchool
              ? <>{tn.lang.schoolAge(this.props.schoolAge, schedule.school)}</>
              : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%'}}>
                  <div>{tn.formatTime(new Date(dataA.handovers[0].time))}</div>
                  {/* Today's enlarged date leaves room for one row only, so drop the place. */}
                  {!isToday && <div style={{textOverflow: 'ellipsis', maxWidth: '100%', overflow: 'hidden', whiteSpace: 'nowrap', display: 'block'}} >
                    {dataA.handovers[0].location || ""}
                  </div>}
                </div>
            : <>?</>);

          // Nudge today's time 2px down so it clears the enlarged date in short cells.
          visit = <div className={classes.custodyVisit}
              style={{ fontWeight: isEditing ? 600 : 400, transform: isToday ? 'translateY(-4px)' : undefined }}>
              {handoverLocation}
            </div>;
        }

        let night: JSX.Element | null = null;
        if (dataA.hasNight) {
          night = (
            <div className={classes.custodyNight} style={{ backgroundColor: colorA, width:(this.props.size !== "large" && !isEditing)?'40%':'15%'}}>
              {isEditing ? <span style={{ paddingLeft: 2, zIndex: 1, fontSize: 10 }}>{parentAName}</span> : ''}
            </div>
          );
        } else if (dataB.hasNight) {
          night = (
            <div className={classes.custodyNight} style={{ backgroundColor: colorB, width:(this.props.size !== "large" && !isEditing)?'40%':'15%'}}>
              {isEditing ? <span style={{ paddingLeft: 2, zIndex: 1, fontSize: 10 }}>{parentBName}</span> : ''}
            </div>
          );
        }

        if (this.props.standaAloneCalendar) {
          events = [];
        }
        return {
          line: (
            <div className={custodyBarStyle}>
              {morning}
              {visit}
              {night}
            </div>
          ),
          events,
          dayType
        };
      }
    }
  }
}

// The handover times and places a visitation day lists under its custody bar
export const handoverRows = (handovers: IHandoverData[], schedule: ISchedule, schoolAge: number, className: string,
  isEditing: boolean): JSX.Element[] => handovers.map((vis) => (
    <div key={vis.time} className={className} style={isEditing ? { fontWeight: 700 } : { fontWeight: 400 }}>
      {!vis.atSchool && <span>{tn.formatTime(new Date(vis.time))}</span>}
      <span>{vis.atSchool ? tn.lang.schoolAge(schoolAge, schedule.school)
        : vis.location && vis.location.length > 0 ? vis.location : tn.lang.unknown}</span>
    </div>
  ));

Day.contextType = CordovaContext;

export default withStyles(MonthviewStyle)(Day);



// WEBPACK FOOTER //
// ./src/components/calendar2/monthView/Day.tsx